import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import debounce from 'lodash.debounce';
import { AutocompletePrediction, AutocompleteService, AutocompleteSessionToken } from './typesGoogle';
import dynamicImport from '../../utils/dynamicImport';
import useDefaultPlaces from './useDefaultPlaces';

export type UsePlacesAutocomplete = {
  places: AutocompletePrediction[];
  searchPlaces: (searchTerm: string) => void;
  hasError: boolean;
  loadSdk: () => void;
  isFetching: boolean;
};

const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

const isEstablishment = <T extends { types: string[] }>(place: T) => place.types.length === 1 && place.types.includes('establishment');

/**
 * Removes places that are "establishments" (business results)
 */
const filterPlaces = <T extends { types: string[] }>(places: T[]): T[] => {
  if (Array.isArray(places)) {
    return places.filter((place) => !isEstablishment(place));
  }
  return [];
};

/**
 * Returns functionality for interfacing with the google maps places api.
 */
const usePlacesAutocomplete = (): UsePlacesAutocomplete => {
  const mounted = useRef(true);
  const searchTerm = useRef('');
  const autocompleteService = useRef<AutocompleteService | null>(null);
  const autocompleteSessionToken = useRef<AutocompleteSessionToken | null>(null);
  const { defaultPlaces } = useDefaultPlaces();
  const [places, setPlaces] = useState<AutocompletePrediction[]>(defaultPlaces);
  const [hasError, setError] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const getPlacePredictions = useMemo(
    () => debounce(() => {
      if (!mounted.current) {
        return;
      }
      const input = searchTerm.current;
      const sessionToken = autocompleteSessionToken.current;
      // Only call the api when we have a search term
      if (!input) {
        setPlaces(defaultPlaces);
        setIsFetching(false);
        return;
      }

      autocompleteService.current?.getPlacePredictions(
        { input, sessionToken },
        (predictions, status) => {
          setIsFetching(false);
          if (status !== google.maps.places.PlacesServiceStatus.OK) {
            setPlaces(defaultPlaces);
            return;
          }

          // Ensure the response for the input is still relevant
          // to prevent network race conditions
          if (input === searchTerm.current) {
            setPlaces(filterPlaces(predictions));
          }
        },
      );
    }, 200),
    [defaultPlaces],
  );

  const loadSdk = useCallback(() => {
    if (typeof window !== 'undefined' && !autocompleteService.current) {
      dynamicImport(() => import(/* webpackChunkName: "loadGoogleMapsSdk" */ 'src/common/utils/loadGoogleMapsSdk').then(
        (module) => module.default,
      )).then((loadGoogleMapsSdk) => {
        loadGoogleMapsSdk(
          {
            key: GOOGLE_MAPS_API_KEY,
            libraries: 'places',
          },
          ({ googleMaps, error }) => {
            // don't proceed if the component has been unmounted
            if (!mounted.current) {
              return;
            }

            if (error) {
              setError(true);
              throw error;
            }

            autocompleteService.current = new googleMaps.places.AutocompleteService();
            autocompleteSessionToken.current = new googleMaps.places.AutocompleteSessionToken();
            getPlacePredictions();
          },
        );
      });
    }
  }, [getPlacePredictions]);

  const searchPlaces = useCallback(
    (term: string) => {
      searchTerm.current = term;
      if (term) {
        setIsFetching(true);
      }
      if (autocompleteService.current) {
        getPlacePredictions();
      } else {
        loadSdk();
      }
    },
    [getPlacePredictions, loadSdk],
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    getPlacePredictions();
  }, [getPlacePredictions]);

  return {
    places,
    searchPlaces,
    hasError,
    loadSdk,
    isFetching,
  };
};

export default usePlacesAutocomplete;
